import { AuthState } from 'contexts/authContext';
import Cache from 'utils/cache';
import BackofficeReportsService from 'services/backofficeReportsService';
import moment from 'moment';

const BackofficeReportsLicenseTypeIds = [8];

function siteFilterFn(node) {
  return node.typeId === 5 && BackofficeReportsLicenseTypeIds.every((i) => node.licenseMap?.[i - 1] === '1');
};

async function getSiteContextHierarchy() {
  const savedHierarchyId = Cache.getPersistant('backofficeReports_hierarchy')
  if (savedHierarchyId) {
    //select 'last used' node saved in session cache
    return savedHierarchyId;
  }
  else {
    const sitesList = AuthState.hierarchy;
    //select 'last used' node saved in database
    const storedHierarchyId = await BackofficeReportsService.API.getUserStore();
    if (storedHierarchyId > 0) {
      if (sitesList.filter((node) => node.id === storedHierarchyId).length > 0) {
        Cache.setPersistant('backofficeReports_hierarchy', storedHierarchyId);
        return storedHierarchyId;
      }
    }
    //select first available node from hierarchy list
    const nodes = sitesList.filter(siteFilterFn);
    if (nodes.length > 0) {
      const newHierarchyId = nodes[0].id;
      Cache.setPersistant('backofficeReports_hierarchy', newHierarchyId);
      return newHierarchyId;
    }
    return null;
  }
}

function setSiteContextHierarchy(id) {
  Cache.setPersistant('backofficeReports_hierarchy', id);
}

async function getUserRuns() {
  const hierarchyId = await getSiteContextHierarchy();
  const runs = Cache.getPersistant('backofficeReports_userRuns');
  if (runs && Cache.getTemporary('backofficeReports_userRunsSiteId') === hierarchyId) {
    return runs;
  }
  else {
    const newRuns = await BackofficeReportsService.API.getUserRuns(hierarchyId);
    Cache.setTemporary('backofficeReports_userRuns', newRuns);
    Cache.setTemporary('backofficeReports_userRunsSiteId', hierarchyId);
    return newRuns;
  }
}

async function getReportTypes() {
  const types = Cache.getPersistant('backofficeReports_types');
  if (types) {
    return types;
  }
  else {
    const newTypes = await BackofficeReportsService.API.getReportTypes();
    Cache.setPersistant('backofficeReports_types', newTypes);
    return newTypes;
  }
}

async function getReportConfigurations() {
  const configurations = Cache.getPersistant('backofficeReports_configurations');
  if (configurations) {
    return configurations;
  }
  else {
    const newConfigurations = await BackofficeReportsService.API.getReportConfigurations();
    Cache.setPersistant('backofficeReports_configurations', newConfigurations);
    return newConfigurations;
  }
}

async function getSiteReportConfigurations(hierarchyId) {
  const configurations = Cache.getTemporary('backofficeReports_siteConfigurations');
  if (configurations && Cache.getTemporary('backofficeReports_siteConfigurationsSiteId') === hierarchyId) {
    return configurations;
  }
  else {
    const newConfigurations = await BackofficeReportsService.API.getSiteReportConfigurations(hierarchyId);
    Cache.setTemporary('backofficeReports_siteConfigurations', newConfigurations);
    Cache.setTemporary('backofficeReports_siteConfigurationsSiteId', hierarchyId);
    return newConfigurations;
  }
}

function getRelativeDateOptions(reportType) {
  if (reportType && reportType.id) {
    switch (reportType.id) {
      case 1:  //Sales - Daily Sales Report
        return [
          { key: 'today', name: 'Today', group: 'Single Day', default: true },
          { key: 'yesterday', name: 'Yesterday', group: 'Single Day' },
          { key: 'daysoff', name: 'Custom Days Ago', group: 'Single Day' },
          { key: 'daysrangeoff', name: 'Custom Days Ago Range', group: 'Multiple Days' },
          { key: 'thisweek', name: 'This Week', group: 'Single Week' },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'weeksoff', name: 'Custom Weeks Ago', group: 'Single Week' },
          { key: 'thismonth', name: 'This Month', group: 'Single Month' },
          { key: 'lastmonth', name: 'Last Month', group: 'Single Month' },
        ];
      case 11: //Sales - Reconciliation Report
      case 16: //Sales - Category Sales Report: Day
      case 45: //Sales - Item Sales Report: Multi-Week
      case 15: //Sales - Sales Statistics Report: Daily
      case 9:  //Sales - Hourly Flash Report
      case 33: //Inventory - Master List Report
      case 34: //Inventory - Vendor List Report
      case 35: //Inventory - Inventory Worksheet
      case 36: //Inventory - Cost of Goods on Hand
      case 37: //Inventory - Recipes
      case 38: //Inventory - Transaction Detail
      case 39: //Inventory - On-Hand Report
      case 40: //Inventory - Re-Order Report
      case 41: //Inventory - Purchase Log Report
      case 42: //Inventory - Food Usage Report
      case 43: //Inventory - Food Usage vs. Sales Report
      case 8:  //Inventory - Food Cost Report
      case 48: //Audit - POSipay Report
        return [
          { key: 'today', name: 'Today', group: 'Single Day', default: true },
          { key: 'yesterday', name: 'Yesterday', group: 'Single Day' },
          { key: 'daysoff', name: 'Custom Days Ago', group: 'Single Day' },
        ];
      case 46: //Sales - Item Sales Report: Day & Item Sales Report: Week
      case 18: //Sales - Discount/Coupon Report
      case 12: //Efficiency - Server Productivity Report
      case 20: //Efficiency - Table Turn Report
      case 24: //Audit - Manager Activity Report
      case 25: //Audit - Account Report
        return [
          { key: 'today', name: 'Today', group: 'Single Day', default: true },
          { key: 'yesterday', name: 'Yesterday', group: 'Single Day' },
          { key: 'daysoff', name: 'Custom Days Ago', group: 'Single Day' },
          { key: 'daysrangeoff', name: 'Custom Days Ago Range', group: 'Multiple Days' },
        ];
      case 47: //Sales - Category Sales Report: Week
      case 10: //Sales - Item Sales Report: Weekly
      case 44: //Sales - Sales Statistics Report: Weekly
      case 19: //Efficiency - Service Time Report
      case 5:  //Time and Attendance - Schedule Report
      case 27: //Time and Attendance - Declared Tips Report
        return [
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'weeksoff', name: 'Custom Weeks Ago', group: 'Single Week' },
        ];
      case 14: //Sales - Weekly Sales Report
        return [
          { key: 'daysrangeoff', name: 'Custom Days Ago Range', group: 'Multiple Days' },
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'weeksoff', name: 'Custom Weeks Ago', group: 'Single Week' },
          { key: 'weekendingyesterday', name: 'Week Ending Yesterday', group: 'Single Week' },
          { key: 'weekendingtoday', name: 'Week Ending Today', group: 'Single Week' },
        ];
      case 21: //Efficiency - VDU Bump Report
      case 4:  //Time and Attendance - Time Report
      case 30: //Time and Attendance - Labor Productivity Report
        return [
          { key: 'today', name: 'Today', group: 'Single Day' },
          { key: 'yesterday', name: 'Yesterday', group: 'Single Day' },
          { key: 'daysoff', name: 'Custom Days Ago', group: 'Single Day' },
          { key: 'daysrangeoff', name: 'Custom Days Ago Range', group: 'Multiple Days' },
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'weeksoff', name: 'Custom Weeks Ago', group: 'Single Week' },
        ];
      case 7:  //Time and Attendance - Exception Report (Hours)
      case 26: //Time and Attendance - Exception Report (Schedule vs Hours)
      case 28: //Time and Attendance - Payroll Report
      case 2:  //Time and Attendance - Labor Distribution Report
        return [
          { key: 'daysrangeoff', name: 'Custom Days Ago Range', group: 'Multiple Days' },
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'bothweeks', name: 'Both Weeks', group: 'Single Week' },
        ];
      case 3:  //Time and Attendance - Overtime Prediction Report
        return [
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
        ];
      case 29: //Time and Attendance - Labor Cost Analysis
        return [
          { key: 'thisweek', name: 'This Week', group: 'Single Week', default: true },
          { key: 'lastweek', name: 'Last Week', group: 'Single Week' },
          { key: 'nextweek', name: 'Next Week', group: 'Single Week' },
        ];
      default:
        return null;
    }
  }
  return null;
}

function getRelativeDateError(reportType, periodName, startOffset, endOffset) {
  if (periodName && periodName.length > 0) {
    switch (periodName) {
      case 'daysrangeoff':
        let days = endOffset - startOffset + 1;
        if (days < 0) days = days * -1;
        return getDateRangeError(reportType, days);
      default:
        return null;
    }
  }
  return null;
}

function getAbsoluteDateOptions(reportType) {
  if (reportType && reportType.id) {
    switch (reportType.id) {
      case 11: //Sales - Reconciliation Report
      case 16: //Sales - Category Sales Report: Day
      case 45: //Sales - Item Sales Report: Multi-Week
      case 15: //Sales - Sales Statistics Report: Daily
      case 9:  //Sales - Hourly Flash Report
      case 33: //Inventory - Master List Report
      case 34: //Inventory - Vendor List Report
      case 35: //Inventory - Inventory Worksheet
      case 36: //Inventory - Cost of Goods on Hand
      case 37: //Inventory - Recipes
      case 38: //Inventory - Transaction Detail
      case 39: //Inventory - On-Hand Report
      case 40: //Inventory - Re-Order Report
      case 41: //Inventory - Purchase Log Report
      case 42: //Inventory - Food Usage Report
      case 43: //Inventory - Food Usage vs. Sales Report
      case 8:  //Inventory - Food Cost Report
      case 48: //Audit - POSipay Report
        return [
          'start',
        ];
      case 1:  //Sales - Daily Sales Report
      case 14: //Sales - Weekly Sales Report
      case 47: //Sales - Category Sales Report: Week
      case 44: //Sales - Sales Statistics Report: Weekly
      case 10: //Sales - Item Sales Report: Week
      case 46: //Sales - Item Sales Report: Period
      case 18: //Sales - Discount/Coupon Report  
      case 12: //Efficiency - Server Productivity Report
      case 20: //Efficiency - Table Turn Report
      case 19: //Efficiency - Service Time Report
      case 21: //Efficiency - VDU Bump Report
      case 24: //Audit - Manager Activity Report
      case 25: //Audit - Account Report
      case 2:  //Time and Attendance - Labor Distribution Report
      case 4:  //Time and Attendance - Time Report
      case 7:  //Time and Attendance - Exception Report (Hours)
      case 26: //Time and Attendance - Exception Report (Schedule vs Hours)
      case 5:  //Time and Attendance - Schedule Report
      case 3:  //Time and Attendance - Overtime Prediction Report
      case 27: //Time and Attendance - Declared Tips Report
      case 28: //Time and Attendance - Payroll Report
      case 29: //Time and Attendance - Labor Cost Analysis
      case 30: //Time and Attendance - Labor Productivity Repor
        return [
          'start',
          'end',
        ];
      default:
        return null;
    }
  }
  return null;
}

function getAbsoluteDateError(reportType, startDate, endDate) {
  var start = moment(startDate).startOf('day');
  var end = moment(endDate).startOf('day');
  var days = end.diff(start, 'days') + 1;
  return getDateRangeError(reportType, days);
}

function getDateRangeError(reportType, days) {
  if (reportType && reportType.id) {
    switch (reportType.id) {
      case 11: //Sales - Reconciliation Report
      case 16: //Sales - Category Sales Report: Day
      case 45: //Sales - Item Sales Report: Multi-Week
      case 15: //Sales - Sales Statistics Report: Daily
      case 9:  //Sales - Hourly Flash Report
      case 33: //Inventory - Master List Report
      case 34: //Inventory - Vendor List Report
      case 35: //Inventory - Inventory Worksheet
      case 36: //Inventory - Cost of Goods on Hand
      case 37: //Inventory - Recipes
      case 38: //Inventory - Transaction Detail
      case 39: //Inventory - On-Hand Report
      case 40: //Inventory - Re-Order Report
      case 41: //Inventory - Purchase Log Report
      case 42: //Inventory - Food Usage Report
      case 43: //Inventory - Food Usage vs. Sales Report
      case 8:  //Inventory - Food Cost Report
      case 48: //Audit - POSipay Report
        return null;
      case 1:  //Sales - Daily Sales Report
      case 14: //Sales - Weekly Sales Report
      case 47: //Sales - Category Sales Report: Week
      case 44: //Sales - Sales Statistics Report: Weekly
      case 10: //Sales - Item Sales Report: Week
      case 46: //Sales - Item Sales Report: Period
      case 18: //Sales - Discount/Coupon Report  
      case 12: //Efficiency - Server Productivity Report
      case 20: //Efficiency - Table Turn Report
      case 19: //Efficiency - Service Time Report
      case 21: //Efficiency - VDU Bump Report
      case 25: //Audit - Account Report
      case 2:  //Time and Attendance - Labor Distribution Report
      case 4:  //Time and Attendance - Time Report
      case 7:  //Time and Attendance - Exception Report (Hours)
      case 26: //Time and Attendance - Exception Report (Schedule vs Hours)
      case 5:  //Time and Attendance - Schedule Report
      case 3:  //Time and Attendance - Overtime Prediction Report
      case 28: //Time and Attendance - Payroll Report
      case 30: //Time and Attendance - Labor Productivity Report
        if (days > 90)
          return `Invalid date range of ${days} days, the range exceeds the maximum of 90 days for this report`;
        return null;
      case 29: //Time and Attendance - Labor Cost Analysis
        if (days !== 7)
          return `Invalid date range of ${days} days, the range must equal 7 days for this report`;
        return null;
      case 24: //Audit - Manager Activity Report
        if (days > 30)
          return `Invalid date range of ${days} days, the range exceeds the maximum of 30 days for this report`;
        return null;
      case 27: //Time and Attendance - Declared Tips Report
        if (days > 7)
          return `Invalid date range of ${days} days, the range exceeds the maximum of 7 days for this report`;
        return null;
      default:
        return null;
    }
  }
  return null;
}

function isAdvancedOptionsAllowed(reportTypeId) {
  switch (reportTypeId) {
    case 1:  //Sales - Daily Sales Report
    case 2:  //Time and Attendance - Labor Distribution Report
    case 28: //Time and Attendance - Payroll Report
      return true;
    default:
      return false;
  }
}

const BackofficeReportsHelper = {
  siteFilterFn,
  getSiteContextHierarchy,
  setSiteContextHierarchy,
  getUserRuns,
  getReportTypes,
  getReportConfigurations,
  getSiteReportConfigurations,
  getRelativeDateOptions,
  getRelativeDateError,
  getAbsoluteDateOptions,
  getAbsoluteDateError,
  isAdvancedOptionsAllowed,
};

export default BackofficeReportsHelper;